import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { aiSalesSearch, getAISalesVideos, getAIToolsStatus, getMyCredits } from '../lib/api';
import { 
  ArrowLeft, 
  Send, 
  User,
  Copy,
  Check,
  RotateCcw,
  ThumbsUp,
  ThumbsDown,
  Play,
  FileText,
  ChevronDown,
  Loader2,
  MessageSquare,
  Zap,
  TrendingUp,
  Shield,
  Handshake,
  ShieldOff,
  Coins
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const AI_SALES_ICON = "https://static.prod-images.emergentagent.com/jobs/cedee861-1d2e-4012-91c4-57a04e6b2415/images/b8822b0525619dcadb8587efc39d3877737dbbf7d62b678cf4ab816d79295ae2.png";

// Sales stages with colors and descriptions
const SALES_STAGES = [
  { id: 'initial', label: 'Initial', icon: '👋', color: 'bg-green-100 text-green-700 border-green-300', desc: 'First contact & qualification' },
  { id: 'discovery', label: 'Discovery', icon: '🔍', color: 'bg-green-100 text-green-700 border-green-300', desc: 'Understanding pain points' },
  { id: 'proposal', label: 'Proposal', icon: '📋', color: 'bg-green-100 text-green-700 border-green-300', desc: 'Presenting solution & value' },
  { id: 'negotiation', label: 'Negotiation', icon: '🤝', color: 'bg-green-100 text-green-700 border-green-300', desc: 'Handling objections' },
  { id: 'closing', label: 'Closing', icon: '🎯', color: 'bg-green-100 text-green-700 border-green-300', desc: 'Getting commitment' }
];

// Suggested prompts based on common sales scenarios
const SUGGESTED_PROMPTS = [
  "How do I handle 'too expensive' objection?",
  "Give me discovery questions for a manufacturing prospect",
  "Help me prepare for a demo meeting tomorrow",
  "What's the best way to create urgency?",
  "How do I compete against SAP?",
  "Write a follow-up email after demo"
];

export default function AISalesPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [prospectStage, setProspectStage] = useState('initial');
  const [messages, setMessages] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [credits, setCredits] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Check if Sales Coach is enabled
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await getAIToolsStatus();
        setIsDisabled(!res.data.ai_sales_coach_enabled);
      } catch { setIsDisabled(false); }
      finally { setCheckingStatus(false); }
    };
    const loadCredits = async () => {
      try { const res = await getMyCredits(); setCredits(res.data.credits); } catch {}
    };
    checkStatus();
    loadCredits();
  }, []);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [query, adjustTextareaHeight]);

  // Scroll handling
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Load videos
  useEffect(() => {
    const loadVideos = async () => {
      try {
        const response = await getAISalesVideos();
        setVideos(response.data || []);
      } catch (error) {
      }
    };
    loadVideos();
  }, []);

  const handleSend = async (customQuery = null) => {
    const messageText = customQuery || query.trim();
    if (!messageText || isLoading) return;

    setQuery('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      stage: prospectStage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content
      }));

      const response = await aiSalesSearch(messageText, conversationHistory, prospectStage);
      const { answer, sources, next_steps, suggested_videos, stage_tips } = response.data;

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: answer,
        sources: sources || [],
        nextSteps: next_steps || [],
        suggestedVideos: suggested_videos || videos.filter(v => v.category === prospectStage || v.category === 'general').slice(0, 2),
        stageTips: stage_tips,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      // Refresh credits
      try { const cr = await getMyCredits(); setCredits(cr.data.credits); } catch {}
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get response';
      const isCredits = errorMsg.includes("credits") || errorMsg.includes("No credits");
      toast.error(isCredits ? "No credits remaining. Contact your admin." : errorMsg);
      
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: isCredits
          ? "You've run out of AI credits. Please contact your administrator to get more credits."
          : "I'm having trouble processing your request. Let's try again - what's the specific situation you need help with?",
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRegenerate = (messageIndex) => {
    const userMessage = messages[messageIndex - 1];
    if (userMessage?.role === 'user') {
      setMessages(prev => prev.slice(0, messageIndex));
      handleSend(userMessage.content);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setQuery('');
  };

  const currentStage = SALES_STAGES.find(s => s.id === prospectStage);

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (isDisabled) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center" data-testid="ai-sales-disabled">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldOff className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">AI Sales Coach is Disabled</h2>
          <p className="text-slate-500 text-sm mb-6">This agent has been temporarily disabled by the administrator. Please check back later or contact support.</p>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] flex flex-col" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="ai-sales-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img 
                src={AI_SALES_ICON} 
                alt="Sales Coach" 
                className="w-8 h-8 object-contain"
              />
              <span className="font-semibold text-gray-900">Sales Coach</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="text-gray-500 hover:text-gray-700 gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                New Chat
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Stage Selector */}
      <div className="bg-white/80 border-b border-gray-100 px-4 py-2 overflow-x-auto">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          <span className="text-xs text-gray-500 whitespace-nowrap mr-2">Stage:</span>
          {SALES_STAGES.map(stage => (
            <button
              key={stage.id}
              onClick={() => setProspectStage(stage.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
                prospectStage === stage.id 
                  ? 'bg-green-100 text-green-700 border-green-300' 
                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
              data-testid={`stage-${stage.id}`}
            >
              <span>{stage.icon}</span>
              <span>{stage.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto"
      >
        {messages.length === 0 ? (
          /* Empty State - Welcome Screen */
          <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/10 p-2">
                <img src={AI_SALES_ICON} alt="Sales Coach" className="w-12 h-12 object-contain" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Ready to close more deals?
              </h1>
              <p className="text-gray-500 max-w-md mx-auto">
                I'm your AI Sales Coach. Tell me about your prospect, objection, or situation - I'll give you actionable strategies to move the deal forward.
              </p>
            </div>

            {/* Stage Info */}
            {currentStage && (
              <div className="mb-8 p-4 rounded-xl border bg-green-50 border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{currentStage.icon}</span>
                  <span className="font-medium text-green-800">{currentStage.label} Stage</span>
                </div>
                <p className="text-sm text-green-700">{currentStage.desc}</p>
              </div>
            )}

            {/* Suggested Prompts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
              {SUGGESTED_PROMPTS.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSend(prompt)}
                  className="group p-4 text-left bg-white border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50/50 transition-all duration-200"
                  data-testid={`suggested-prompt-${idx}`}
                >
                  <p className="text-sm text-gray-700 group-hover:text-green-700">
                    {prompt}
                  </p>
                </button>
              ))}
            </div>

            {/* Capabilities */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-white border border-gray-100">
                <Shield className="w-5 h-5 text-green-500 mb-2" />
                <h3 className="font-medium text-gray-900 text-sm mb-1">Objection Handling</h3>
                <p className="text-xs text-gray-500">Battlecards for price, competition, timing objections</p>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100">
                <Zap className="w-5 h-5 text-green-500 mb-2" />
                <h3 className="font-medium text-gray-900 text-sm mb-1">Scripts & Tactics</h3>
                <p className="text-xs text-gray-500">Discovery questions, demos, closing techniques</p>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100">
                <Handshake className="w-5 h-5 text-green-500 mb-2" />
                <h3 className="font-medium text-gray-900 text-sm mb-1">Deal Strategy</h3>
                <p className="text-xs text-gray-500">Negotiation, urgency, proposal optimization</p>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="max-w-3xl mx-auto px-4 py-6">
            {messages.map((message, idx) => (
              <div 
                key={message.id} 
                className={`mb-6 ${message.role === 'user' ? 'flex justify-end' : ''}`}
              >
                {message.role === 'user' ? (
                  /* User Message */
                  <div className="flex items-start gap-3 max-w-[85%]">
                    <div className="bg-green-600 text-white px-4 py-3 rounded-2xl rounded-tr-md">
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      {message.stage && (
                        <span className="inline-block mt-2 text-xs opacity-70">
                          {SALES_STAGES.find(s => s.id === message.stage)?.icon} {SALES_STAGES.find(s => s.id === message.stage)?.label}
                        </span>
                      )}
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                ) : (
                  /* Assistant Message */
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 p-1">
                      <img src={AI_SALES_ICON} alt="" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`prose prose-sm max-w-none ${message.isError ? 'text-red-600' : 'text-gray-800'}`}>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
                            ul: ({ children }) => <ul className="mb-3 ml-4 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="mb-3 ml-4 space-y-1 list-decimal">{children}</ol>,
                            li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                            code: ({ children }) => <code className="px-1.5 py-0.5 bg-gray-100 rounded text-sm font-mono text-green-700">{children}</code>,
                            h2: ({ children }) => <h2 className="font-semibold text-gray-900 text-lg mt-4 mb-2">{children}</h2>,
                            h3: ({ children }) => <h3 className="font-semibold text-gray-900 mt-4 mb-2">{children}</h3>,
                            blockquote: ({ children }) => <blockquote className="border-l-2 border-green-500 pl-4 italic text-gray-600">{children}</blockquote>,
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>

                      {/* Stage Tips */}
                      {message.stageTips && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs font-medium text-amber-700 mb-1">💡 Stage Tip</p>
                          <p className="text-xs text-amber-600">{message.stageTips}</p>
                        </div>
                      )}

                      {/* Next Steps */}
                      {message.nextSteps?.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> Action Items
                          </p>
                          {message.nextSteps.map((step, sIdx) => (
                            <div 
                              key={sIdx}
                              className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded-lg"
                            >
                              <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-xs text-white font-medium">{sIdx + 1}</span>
                              </div>
                              <span className="text-sm text-gray-700">{step}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Suggested Videos */}
                      {message.suggestedVideos?.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-medium text-gray-500">🎬 Training Videos</p>
                          {message.suggestedVideos.slice(0, 2).map((video, vIdx) => (
                            <a
                              key={vIdx}
                              href={video.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                            >
                              <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                                <Play className="w-4 h-4 text-red-600" />
                              </div>
                              <span className="text-sm text-gray-700">{video.title}</span>
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Sources */}
                      {message.sources?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {message.sources.map((source, sIdx) => (
                            <span 
                              key={sIdx}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs"
                            >
                              <FileText className="w-3 h-3" />
                              {source.name}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-3 flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(message.content, message.id)}
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          {copiedId === message.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerate(idx)}
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          <ThumbsUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          <ThumbsDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-start gap-3 mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center p-1">
                  <img src={AI_SALES_ICON} alt="" className="w-6 h-6 object-contain" />
                </div>
                <div className="flex items-center gap-2 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm text-gray-500">Analyzing your situation...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Scroll to Bottom Button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-28 right-8 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <ChevronDown className="w-5 h-5 text-gray-600" />
        </button>
      )}

      {/* Input Area */}
      <div className="sticky bottom-0 bg-gradient-to-t from-[#f9fafb] via-[#f9fafb] to-transparent pt-4 pb-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="relative bg-white border border-gray-200 rounded-2xl shadow-sm focus-within:border-green-400 focus-within:ring-2 focus-within:ring-green-100 transition-all">
            <textarea
              ref={textareaRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your sales situation or ask for advice..."
              disabled={isLoading}
              rows={1}
              className="w-full px-4 py-3 pr-12 bg-transparent resize-none focus:outline-none text-gray-900 placeholder:text-gray-400 text-sm max-h-48"
              data-testid="ai-sales-input"
            />
            <Button
              onClick={() => handleSend()}
              disabled={isLoading || !query.trim()}
              size="icon"
              className="absolute right-2 bottom-2 w-8 h-8 bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 rounded-lg transition-colors"
              data-testid="ai-sales-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-center text-xs text-gray-400 mt-2">
            Powered by ABSOLIN Sales Knowledge Base
            {credits !== null && (
              <span className={`ml-2 inline-flex items-center gap-1 ${credits <= 5 ? "text-red-500" : credits <= 20 ? "text-amber-500" : "text-slate-400"}`} data-testid="credits-remaining">
                <Coins className="w-3 h-3" /> {credits} credits remaining
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
